/**
 * Image compression utility for reducing file sizes before upload
 * Uses Canvas API for client-side compression
 */

interface CompressionOptions {
  maxSizeMB?: number;
  maxWidthOrHeight?: number;
  useWebWorker?: boolean;
  quality?: number;
  fileType?: string;
}

/**
 * Compress an image file to reduce its size
 * @param file - The image file to compress
 * @param options - Compression options
 * @returns Promise<File> - The compressed image file
 */
export async function compressImage(
  file: File,
  options: CompressionOptions = {}
): Promise<File> {
  const {
    maxSizeMB = 1, // Target max size in MB (default 1MB)
    maxWidthOrHeight = 1920, // Max width or height in pixels
    quality = 0.8, // JPEG quality (0-1)
    fileType = file.type || "image/jpeg",
  } = options;

  // Don't compress if file is already small enough
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  if (file.size <= maxSizeBytes && !options.maxWidthOrHeight) {
    console.log(
      `File ${file.name} is already under ${maxSizeMB}MB, skipping compression`
    );
    return file;
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const img = new Image();

      img.onload = () => {
        try {
          // Calculate new dimensions
          let { width, height } = img;

          if (width > maxWidthOrHeight || height > maxWidthOrHeight) {
            if (width > height) {
              height = (height / width) * maxWidthOrHeight;
              width = maxWidthOrHeight;
            } else {
              width = (width / height) * maxWidthOrHeight;
              height = maxWidthOrHeight;
            }
          }

          // Create canvas for compression
          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext("2d");
          if (!ctx) {
            reject(new Error("Failed to get canvas context"));
            return;
          }

          // Draw and compress image
          ctx.drawImage(img, 0, 0, width, height);

          // Convert to blob with quality setting
          canvas.toBlob(
            async (blob) => {
              if (!blob) {
                reject(new Error("Failed to compress image"));
                return;
              }

              // If still too large, recursively compress with lower quality
              if (blob.size > maxSizeBytes && quality > 0.1) {
                console.log(
                  `Image still ${(blob.size / 1024 / 1024).toFixed(
                    2
                  )}MB, reducing quality...`
                );
                const recursiveFile = new File([blob], file.name, {
                  type: fileType,
                });
                const recompressed = await compressImage(recursiveFile, {
                  ...options,
                  quality: quality - 0.1,
                });
                resolve(recompressed);
                return;
              }

              // Create new File object with compressed data
              const compressedFile = new File([blob], file.name, {
                type: fileType,
                lastModified: Date.now(),
              });

              console.log(
                `Compressed ${file.name}: ${(file.size / 1024 / 1024).toFixed(
                  2
                )}MB → ${(compressedFile.size / 1024 / 1024).toFixed(2)}MB`
              );

              resolve(compressedFile);
            },
            fileType,
            quality
          );
        } catch (error) {
          reject(error);
        }
      };

      img.onerror = () => {
        reject(new Error("Failed to load image"));
      };

      img.src = e.target?.result as string;
    };

    reader.onerror = () => {
      reject(new Error("Failed to read file"));
    };

    reader.readAsDataURL(file);
  });
}

/**
 * Compress multiple images in parallel
 * @param files - Array of image files to compress
 * @param options - Compression options
 * @returns Promise<File[]> - Array of compressed image files
 */
export async function compressImages(
  files: File[],
  options: CompressionOptions = {}
): Promise<File[]> {
  const compressionPromises = files.map((file) =>
    compressImage(file, options).catch((error) => {
      console.error(`Failed to compress ${file.name}:`, error);
      // Return original file if compression fails
      return file;
    })
  );

  return Promise.all(compressionPromises);
}

/**
 * Calculate the estimated compressed size
 * @param originalSizeMB - Original file size in MB
 * @param quality - Compression quality (0-1)
 * @returns Estimated compressed size in MB
 */
export function estimateCompressedSize(
  originalSizeMB: number,
  quality = 0.8
): number {
  // Rough estimation based on quality
  // This is approximate and actual results may vary
  const compressionRatio = 1 - (1 - quality) * 0.7;
  return originalSizeMB * compressionRatio;
}

/**
 * Get file size in human-readable format
 * @param bytes - File size in bytes
 * @returns Formatted string (e.g., "2.5 MB", "500 KB")
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
}

/**
 * Check if a file is an image
 * @param file - File to check
 * @returns boolean indicating if file is an image
 */
export function isImageFile(file: File): boolean {
  return file.type.startsWith("image/");
}

/**
 * Default compression settings for different use cases
 */
export const CompressionPresets = {
  // High quality, moderate compression
  HIGH_QUALITY: {
    maxSizeMB: 2,
    maxWidthOrHeight: 2048,
    quality: 0.9,
  },

  // Balanced quality and size
  BALANCED: {
    maxSizeMB: 1,
    maxWidthOrHeight: 1920,
    quality: 0.8,
  },

  // Optimized for mobile upload
  MOBILE_OPTIMIZED: {
    maxSizeMB: 0.5,
    maxWidthOrHeight: 1280,
    quality: 0.7,
  },

  // Thumbnail generation
  THUMBNAIL: {
    maxSizeMB: 0.1,
    maxWidthOrHeight: 400,
    quality: 0.6,
  },
};
